import { makeStyles } from '@map/theme';
import { ThemeColorsTypes } from '@map/types';
import { SvgIconProps } from '@mui/material';
import ButtonBase, {
  ButtonBaseProps,
  ButtonBaseTypeMap,
  ExtendButtonBase,
} from '@mui/material/ButtonBase';
import { useFormatMessage } from '@shared/hooks/intl';
import { typePreservingForwardRef } from '@shared/util/preserveForwardRef';
import { DEV } from 'esm-env';
import { ElementType, ReactElement, Ref, cloneElement, useMemo } from 'react';
import { isElement } from 'react-is';

const typographySizes = {
  small: 'subtitle3',
  medium: 'subtitle1',
  large: 'h6',
} as const;

const buttonPaddings = {
  small: [2, 4],
  medium: [2.75, 6],
  large: [3.5, 6],
} satisfies Record<Size, [number, number]>;

type ColorType = Exclude<ThemeColorsTypes, 'neutrals'>;
type StyleProps = {
  color: ColorType;
  size: Size;
  variant: Variant;
  icon?: boolean;
};
const useStyles = makeStyles(
  (
    { borderRadius, palette, spacing, sizing, typography, transitions },
    { color, size, icon }: StyleProps,
  ) => ({
    root: {
      borderRadius: borderRadius('infinite'),
      gap: spacing(size === 'large' ? 2 : 1),
      transition: transitions.create(['background-color', 'color', 'border-color']),
      ...typography[typographySizes[size]],
      padding: spacing(...buttonPaddings[size]),
      ...(!icon && {
        minWidth: sizing(size === 'small' ? 20 : 25),
      }),
    },
    primary: {
      color: palette.common.white,
      backgroundColor: palette[color][500],
      '@media (hover: hover)': {
        '&:hover': {
          backgroundColor: palette[color][700],
        },
      },
      '&.Mui-disabled': {
        color: palette.common.white,
        backgroundColor: palette.neutrals[600],
      },
      '&[data-inverse]': {
        backgroundColor: palette.common.white,
        color: palette[color][500],
        '&:hover': {
          color: palette.common.white,
          backgroundColor: palette[color][700],
        },
        '&.Mui-disabled': {
          color: palette.neutrals[600],
          backgroundColor: palette.neutrals[500],
        },
      },
    },
    secondary: {
      padding: spacing(buttonPaddings[size][0] - 0.5, buttonPaddings[size][1] - 0.5),
      color: palette[color][500],
      border: `2px solid ${palette.neutrals[600]}`,
      '@media (hover: hover)': {
        '&:hover': {
          color: palette.common.white,
          backgroundColor: palette[color][700],
          borderColor: 'transparent',
        },
      },
      '&.Mui-disabled': {
        color: palette.neutrals[600],
      },
      '&[data-inverse]': {
        color: palette.common.white,
        borderColor: palette.common.white,
        '&:hover': {
          backgroundColor: palette[color][700],
          borderColor: 'transparent',
        },
        '&.Mui-disabled': {
          color: palette.neutrals[600],
          borderColor: palette.neutrals[600],
        },
      },
    },
    tertiary: {
      color: palette[color][500],
      // we want to remove the padding from the button but maintain the clickable area bigger
      padding: 0,
      position: 'relative',
      minWidth: 'unset',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: spacing(-buttonPaddings[size][0]),
        bottom: spacing(-buttonPaddings[size][0]),
        left: spacing(-buttonPaddings[size][icon ? 0 : 1]),
        right: spacing(-buttonPaddings[size][icon ? 0 : 1]),
        borderRadius: 'inherit',
      },
      '@media (hover: hover)': {
        '&:hover, &:focus-visible': {
          backgroundColor: 'transparent',
          color: palette[color][700],
        },
      },
      '&.Mui-disabled': {
        color: palette.neutrals[600],
      },
      '&[data-inverse]': {
        color: palette.common.white,
        '&:hover': {
          color: palette[color][300],
        },
        '&.Mui-disabled': {
          color: palette.neutrals[600],
        },
      },
    },
    fullWidth: {
      width: '100%',
    },
    icon: {
      padding: spacing(buttonPaddings[size][0]),
    },
  }),
);

type Size = 'small' | 'medium' | 'large';
type Variant = 'primary' | 'secondary' | 'tertiary';

type CustomButtonProps = {
  size?: Size;
  variant?: Variant;
  startIcon?: ReactElement<SvgIconProps, any>;
  endIcon?: ReactElement<SvgIconProps, any>;
  icon?: boolean;
  inverse?: boolean;
  fullWidth?: boolean;
  color?: Exclude<ThemeColorsTypes, 'neutrals'>;
  ariaLabelIntlKey?: IntlMessageKey;
  /** @deprecated This prop is added just to reconcile the types between the Button component & the HRDF Button component */
  responsive?: boolean;
};

type ButtonProps<TComponent extends ElementType = 'button'> = ButtonBaseProps<
  TComponent,
  CustomButtonProps
>;

const Button: ExtendButtonBase<ButtonBaseTypeMap<ButtonProps>> = ((
  {
    variant = 'primary' as const,
    icon = false,
    inverse = false,
    fullWidth = false,
    color = 'primary',
    size = 'small',
    startIcon,
    endIcon,
    className,
    children,
    focusRipple = true,
    disableRipple,
    ariaLabelIntlKey,
    ...rest
  }: ButtonBaseTypeMap<ButtonProps>['props'] & { className?: string },
  ref: Ref<HTMLButtonElement>,
) => {
  const t = useFormatMessage();
  const { classes, cx } = useStyles({ color, size, variant, icon });

  const translatedKey = useMemo(() => {
    if (ariaLabelIntlKey) {
      return t(ariaLabelIntlKey);
    }
  }, [ariaLabelIntlKey, t]);
  const iconSize =
    size === 'large' || (icon && variant === 'tertiary' && size !== 'small') ? 'medium' : 'small';

  return (
    <ButtonBase
      {...rest}
      ref={ref}
      focusRipple={focusRipple}
      disableRipple={disableRipple || variant === 'tertiary'}
      className={cx(
        `Button-${variant}`,
        classes.root,
        {
          primary: classes.primary,
          secondary: classes.secondary,
          tertiary: classes.tertiary,
        }[variant],
        fullWidth && classes.fullWidth,
        icon && variant !== 'tertiary' && classes.icon,
        className,
      )}
      data-inverse={inverse ? true : undefined}
      aria-label={translatedKey || rest['aria-label']}
    >
      {startIcon && cloneElement(startIcon, { fontSize: iconSize, ...{ 'data-starticon': true } })}
      {icon && isElement(children)
        ? cloneElement(children as ReactElement, { fontSize: iconSize })
        : children}
      {endIcon && cloneElement(endIcon, { fontSize: iconSize, ...{ 'data-endicon': true } })}
    </ButtonBase>
  );
}) as any; // Nasty solution to make TypeScript happy

if (DEV) {
  (Button as any).displayName = 'Button';
}

export default typePreservingForwardRef(Button);
